import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { doOpenCall } from '../../_utilities/doOpenCall'
import type { CalendarBooking } from '../../_types/calendar-booking'
import type { CalendarEventType } from '../../_types/calendar-event-type'
import {
  formatLongDateInTimezone,
  formatTimeInTimezone,
} from '../../_utilities/formatDateTime'
import { detectBrowserTimezone } from '../../_data/timezones'
import { getLocationLabel } from '../booking-page/helpers'
import {
  BookingSummaryPanel,
  ButtonRow,
  CancelledHeadline,
  CancelledSubline,
  Card,
  Centered,
  DangerButton,
  ErrorState,
  FieldLabel,
  Heading,
  Lead,
  PageShell,
  ReasonTextarea,
  SecondaryButton,
  SummaryLabel,
  SummaryValue,
} from './styled'

interface BookingWithEventType {
  booking: CalendarBooking
  eventType: CalendarEventType
}

type CancelPageStep = 'confirming' | 'cancelled'

const CancelPage = (): JSX.Element => {
  const parameters = useParams<{ token: string }>()
  const [data, setData] = useState<BookingWithEventType | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [errorMessage, setErrorMessage] = useState<string>('')

  const [step, setStep] = useState<CancelPageStep>('confirming')
  const [reason, setReason] = useState<string>('')
  const [isCancelling, setIsCancelling] = useState<boolean>(false)
  const [cancelError, setCancelError] = useState<string>('')

  const browserTimezone = useMemo(() => detectBrowserTimezone(), [])

  const loadBooking = useCallback(async (): Promise<void> => {
    if (!parameters.token) {
      setErrorMessage('Cancel link is missing a token.')
      setIsLoading(false)
      return
    }
    setIsLoading(true)
    try {
      const response = await doOpenCall('publiccalendar-getbookingbytoken', {
        token: parameters.token,
        kind: 'cancel',
      })
      if (response.code === 200) {
        setData(JSON.parse(response.data) as BookingWithEventType)
        setErrorMessage('')
      } else {
        setErrorMessage(response.message || 'Booking not found.')
      }
    } catch {
      setErrorMessage('Could not reach the booking service. Try again shortly.')
    } finally {
      setIsLoading(false)
    }
  }, [parameters.token])

  useEffect(() => {
    loadBooking()
  }, [loadBooking])

  const handleConfirmCancel = useCallback(async (): Promise<void> => {
    if (!data) {
      return
    }
    setIsCancelling(true)
    setCancelError('')
    try {
      const response = await doOpenCall('publiccalendar-cancelbooking', {
        cancelToken: data.booking.cancelToken,
        cancelledBy: 'guest',
        reason: reason.trim(),
      })
      if (response.code === 200) {
        setStep('cancelled')
      } else {
        setCancelError(response.message || 'Failed to cancel booking.')
      }
    } catch {
      setCancelError('Could not reach the booking service. Try again shortly.')
    } finally {
      setIsCancelling(false)
    }
  }, [data, reason])

  if (isLoading) {
    return (
      <PageShell>
        <Centered>Loading your booking…</Centered>
      </PageShell>
    )
  }

  if (errorMessage.length > 0 || !data) {
    return (
      <PageShell>
        <ErrorState>{errorMessage || 'Booking not found.'}</ErrorState>
      </PageShell>
    )
  }

  const timezone = data.eventType.timezone || browserTimezone

  if (step === 'cancelled') {
    return (
      <PageShell>
        <Card>
          <CancelledHeadline>This booking is cancelled.</CancelledHeadline>
          <CancelledSubline>
            We&apos;ve let the host know. If you&apos;d like to book a new
            time, head back to the original booking link.
          </CancelledSubline>
        </Card>
      </PageShell>
    )
  }

  return (
    <PageShell>
      <Card>
        <Heading>Cancel this booking?</Heading>
        <Lead>
          The host will be notified. You can rebook any time using your
          original booking link.
        </Lead>
        <BookingSummaryPanel>
          <SummaryLabel>What</SummaryLabel>
          <SummaryValue>{data.eventType.title}</SummaryValue>
          <SummaryLabel>When</SummaryLabel>
          <SummaryValue>
            {formatLongDateInTimezone(data.booking.startTime, timezone)}
            <br />
            {formatTimeInTimezone(data.booking.startTime, timezone)}
            {' – '}
            {formatTimeInTimezone(data.booking.endTime, timezone)} ({timezone})
          </SummaryValue>
          {getLocationLabel(data.eventType).length > 0 ? (
            <>
              <SummaryLabel>Where</SummaryLabel>
              <SummaryValue>{getLocationLabel(data.eventType)}</SummaryValue>
            </>
          ) : null}
        </BookingSummaryPanel>

        <div>
          <FieldLabel htmlFor="cancel-reason">
            Reason (optional)
          </FieldLabel>
          <ReasonTextarea
            id="cancel-reason"
            value={reason}
            placeholder="Let the host know what changed, if you'd like."
            onChange={changeEvent => setReason(changeEvent.target.value)}
          />
        </div>

        {cancelError.length > 0 ? (
          <ErrorState>{cancelError}</ErrorState>
        ) : null}

        <ButtonRow>
          <SecondaryButton
            href={`/reschedule/${data.booking.rescheduleToken}`}
          >
            Reschedule instead
          </SecondaryButton>
          <DangerButton
            type="button"
            onClick={handleConfirmCancel}
            disabled={isCancelling}
          >
            {isCancelling ? 'Cancelling…' : 'Cancel this booking'}
          </DangerButton>
        </ButtonRow>
      </Card>
    </PageShell>
  )
}

export default CancelPage
